import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Tooltip,
  Icon,
  Flex,
  Box,
} from "@chakra-ui/react";
import Select from "react-select";
import {
  newBorder,
  white,
  primary,
  textPrimary,
  hoverItemColor,
  textSecondary,
  primaryAlt,
  backgroundLight,
} from "../../utils/colors";
import { useTranslation } from "react-i18next";
import { shade } from "polished";
import { FaCircleInfo } from "react-icons/fa6";

const FormControlReimbursementType = ({
  label,
  labelFontSize,
  value,
  onChange,
  isMulti,
  isRequired,
  placeholder,
  isTouched,
  isError,
  errorMessage,
  isSearchable,
  w,
  isSmall,
  explainTooltip,
  explainTooltipLabel,
  operatingCountry,
}) => {
  const { t } = useTranslation();
  console.log("operatingCountry", operatingCountry);

  const publicLabelsByCountry = {
    GB: t("reimbursement.gbPublic"), // NHS
    LT: t("reimbursement.ltPublic"), // Ligonių kasos
    US: t("reimbursement.usPublic"), // Medicare/Medicaid
    DE: t("reimbursement.dePublic"), // Gesetzliche Krankenversicherung
    FR: t("reimbursement.frPublic"), // Assurance Maladie
    SE: t("reimbursement.sePublic"), // Försäkringskassan
    NL: t("reimbursement.nlPublic"), // Basisverzekering
    LV: t("reimbursement.lvPublic"), // Nacionālais veselības dienests
    EE: t("reimbursement.eePublic"), // Haigekassa
    PL: t("reimbursement.plPublic"), // Narodowy Fundusz Zdrowia
  };

  const generateStateOptions = () => {
    const options = [];

    const publicLabel =
      publicLabelsByCountry[operatingCountry] || t("reimbursement.public");

    options.push({
      value: "public",
      label: publicLabel,
    });

    options.push({
      value: "insurance",
      label: t("reimbursement.insurance"),
    });

    return options;
  };

  const stateOptions = generateStateOptions();

  // CUSTOM STYLES
  const customStyles = {
    placeholder: (provided) => ({
      ...provided,
      color: "#718096", // Change this to your desired placeholder color
    }),
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: isError ? "red" : state.isFocused ? newBorder : newBorder,
      backgroundColor: white,
      border: `1px solid ${newBorder}`,
      fontSize: isSmall ? "0.875rem" : "0.938rem",
      fontWeight: "500",
      minHeight: isSmall ? "2.5rem" : "3rem",
      height: isMulti ? "initial" : isSmall ? "2.5rem" : "3rem",
      margin: "0",
      padding: "0 8px",
      borderRadius: "0.75rem",

      ":focus-visible": {
        borderColor: primary,
        boxShadow: `0 0 0 1px ${newBorder}`,
      },
      ":hover": {
        boxShadow: "none",
        outline: "none",
        cursor: "pointer",
        borderColor: "#cbd5e0",
      },
    }),

    menu: (provided) => ({
      ...provided,
      borderRadius: "0.75rem",
      boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.3)",
      border: `0px solid ${newBorder}`,
      overflow: "hidden",
      zIndex: 90000,
    }),

    menuList: (provided) => ({
      ...provided,
      margin: "0",
      padding: "0",
      border: "none",
      boxShadow: "none",
    }),

    singleValue: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.5 : 1,
      color: textPrimary,
      margin: "0",
      padding: "0 0px",

      ":hover": {
        bg: "red",
      },
    }),

    option: (provided, state) => ({
      ...provided,

      // backgroundColor: state.isFocused ? backgroundLight : null,
      backgroundColor: state.isSelected ? primary : white, // Background color of selected option
      // color: state.isFocused ? textPrimary : state.data.color,
      color: state.isSelected ? "white" : textSecondary, // Color of text in options
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      margin: "0",
      padding: "0 1rem",
      fontWeight: "500",
      height: "48px",
      fontSize: "0.875rem",
      borderBottom: `1px solid ${newBorder}`,
      ":hover": {
        backgroundColor: state.isSelected
          ? shade(0.1, primary)
          : hoverItemColor,
        color: state.isSelected ? "white" : textPrimary,
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: primary,
      color: white,
      fontSize: "0.938rem",
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      color: state.data.color,
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      ":hover": {
        backgroundColor: state.data.bgDark,
        color: state.data.color,
      },
    }),
    groupHeading: (provided) => ({
      ...provided,
      fontSize: "11px",
      color: textSecondary,
      fontWeight: "500",
    }),
  };

  // LOG

  // console.log("stateOptions", stateOptions);

  return (
    <FormControl
      mb={4}
      isRequired={isRequired}
      isInvalid={isTouched && isError}
      w={w}
    >
      <FormLabel
        fontSize={labelFontSize ? labelFontSize : "sm"}
        position="relative"
        color={textPrimary}
        // w="100%"
      >
        {label ? (
          <Box align="center" width="initial" display="inline-block">
            <Flex align="center">
              {label}
              {explainTooltip && (
                <Tooltip
                  p="0.5rem 1rem"
                  placement="top"
                  borderRadius="1rem"
                  label={explainTooltipLabel ? explainTooltipLabel : "-"}
                >
                  <Flex
                    align="center"
                    ml={1}
                    color={shade(0.3, backgroundLight)}
                    _hover={{ cursor: "pointer", color: primaryAlt }}
                  >
                    <Icon as={FaCircleInfo} fontSize="0.875rem" />
                  </Flex>
                </Tooltip>
              )}
            </Flex>
          </Box>
        ) : null}

        {isError && isTouched && (
          <div>
            <FormErrorMessage fontSize="xs">{errorMessage}</FormErrorMessage>
          </div>
        )}
      </FormLabel>

      <Select
        isMenuOpen
        isSearchable={isSearchable}
        value={
          value ? stateOptions.find((option) => option.value === value) : null
        }
        onChange={(selected) => onChange(selected?.value)}
        options={stateOptions}
        placeholder={placeholder}
        // menuPortalTarget={document.body}
        styles={customStyles}
      />
    </FormControl>
  );
};

export default FormControlReimbursementType;
